<template>
  <!-- "Your download is starting" pop-up: opens on any installer click (see
       src/downloads.js), shows the three steps from download to an active trial and
       the installer prompt the visitor's system may show. Mounted once in App.vue. -->
  <div
    v-if="platform"
    class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 px-4"
    @click.self="close"
  >
    <div
      ref="dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="t.title"
      tabindex="-1"
      class="relative w-full max-w-[560px] max-h-[90vh] overflow-y-auto bg-[#111111] border-2 border-[#FF0000] rounded-2xl p-6 md:p-8 text-left font-helvetica text-[#DDDDDD] shadow-2xl"
    >
      <button
        type="button"
        class="absolute top-3 right-4 text-[#999999] hover:text-white text-[28px] leading-none"
        :aria-label="t.close"
        @click="close"
      >
        &times;
      </button>

      <h2 class="font-russo text-white text-[22px] md:text-[26px] pr-8">
        {{ t.title }}
      </h2>
      <p class="text-[#A9A9A9] text-[15px] mt-1">
        {{ t.subtitle.replace('{os}', installer.label) }}
      </p>

      <ol class="mt-5 flex flex-col gap-3 text-[16px] md:text-[17px]">
        <li v-for="(step, i) in steps" :key="i" class="flex gap-3">
          <span
            class="shrink-0 w-7 h-7 rounded-full bg-[#FF0000] text-white font-bold flex items-center justify-center text-[15px]"
            >{{ i + 1 }}</span
          >
          <span v-html="step"></span>
        </li>
      </ol>

      <p
        class="mt-5 text-[14px] md:text-[15px] bg-[#1f1f1f] border border-[#333333] rounded-lg p-3"
        v-html="t.notes[platform]"
      ></p>

      <p class="mt-4 text-[14px] text-[#999999]">
        {{ t.didntStart }}
        <a :href="installer.url" class="text-[#FF0000] font-bold hover:underline">{{
          t.clickHere
        }}</a>
      </p>
    </div>
  </div>
</template>

<script>
import { INSTALLERS, DOWNLOAD_STARTED_EVENT } from '../downloads';

const TEXT = {
  en: {
    title: 'Your Download Is Starting',
    subtitle: 'Rally Navigator for {os}',
    steps: [
      'Open <b class="text-white">{file}</b> from your Downloads folder and install Rally Navigator.',
      'Launch Rally Navigator and click <b class="text-white">My Subscription → Login / Register</b> to create your account.',
      'Click the link in the <b class="text-white">activation email</b> we send you (check your spam folder) — then log in and your free 7-day trial is ready.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows:</b> if you see “Windows protected your PC”, click <b class="text-white">More info → Run anyway</b>. The installer is signed by Rally Navigator, LLC.',
      mac: '<b class="text-white">Mac:</b> open the .dmg and drag Rally Navigator into Applications. The first time you open it, macOS asks to confirm an app downloaded from the internet — click <b class="text-white">Open</b>. It’s code-signed and Apple-notarized.',
      linux:
        '<b class="text-white">Linux:</b> make the AppImage executable — right-click it → Properties → Permissions → <b class="text-white">Allow executing file as program</b> (or <code>chmod +x</code>) — then double-click it.',
    },
    didntStart: 'Download didn’t start?',
    clickHere: 'Click here',
    close: 'Close',
  },
  fr: {
    title: 'Votre téléchargement commence',
    subtitle: 'Rally Navigator pour {os}',
    steps: [
      'Ouvrez <b class="text-white">{file}</b> depuis votre dossier Téléchargements et installez Rally Navigator.',
      'Lancez Rally Navigator, cliquez sur <b class="text-white">Mon abonnement</b>, puis sur <b class="text-white">Créer un compte</b>.',
      'Cliquez sur le lien de l’<b class="text-white">e-mail d’activation</b> que nous vous envoyons (vérifiez vos spams) — puis connectez-vous : votre essai gratuit de 7 jours est prêt.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows :</b> si « Windows a protégé votre ordinateur » s’affiche, cliquez sur <b class="text-white">Informations complémentaires → Exécuter quand même</b>. Le programme d’installation est signé par Rally Navigator, LLC.',
      mac: '<b class="text-white">Mac :</b> ouvrez le .dmg et faites glisser Rally Navigator dans Applications. À la première ouverture, macOS demande de confirmer l’ouverture d’une app téléchargée sur Internet — cliquez sur <b class="text-white">Ouvrir</b>. L’app est signée et notarisée par Apple.',
      linux:
        '<b class="text-white">Linux :</b> rendez l’AppImage exécutable — clic droit → Propriétés → Permissions → <b class="text-white">Autoriser l’exécution du fichier comme un programme</b> (ou <code>chmod +x</code>) — puis double-cliquez dessus.',
    },
    didntStart: 'Le téléchargement n’a pas démarré ?',
    clickHere: 'Cliquez ici',
    close: 'Fermer',
  },
  es: {
    title: 'Tu descarga está empezando',
    subtitle: 'Rally Navigator para {os}',
    steps: [
      'Abre <b class="text-white">{file}</b> desde tu carpeta de Descargas e instala Rally Navigator.',
      'Abre Rally Navigator, haz clic en <b class="text-white">Mi Suscripción</b> y luego en <b class="text-white">Crear Cuenta</b>.',
      'Haz clic en el enlace del <b class="text-white">correo de activación</b> que te enviamos (revisa el spam) — después inicia sesión: tu prueba gratuita de 7 días está lista.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows:</b> si aparece «Windows protegió su PC», haz clic en <b class="text-white">Más información → Ejecutar de todas formas</b>. El instalador está firmado por Rally Navigator, LLC.',
      mac: '<b class="text-white">Mac:</b> abre el .dmg y arrastra Rally Navigator a Aplicaciones. La primera vez que lo abras, macOS te pedirá confirmar que quieres abrir una app descargada de Internet — haz clic en <b class="text-white">Abrir</b>. Está firmada y notarizada por Apple.',
      linux:
        '<b class="text-white">Linux:</b> haz ejecutable el AppImage — clic derecho → Propiedades → Permisos → <b class="text-white">Permitir ejecutar el archivo como un programa</b> (o <code>chmod +x</code>) — y luego haz doble clic.',
    },
    didntStart: '¿No ha empezado la descarga?',
    clickHere: 'Haz clic aquí',
    close: 'Cerrar',
  },
  it: {
    title: 'Il download sta iniziando',
    subtitle: 'Rally Navigator per {os}',
    steps: [
      'Apri <b class="text-white">{file}</b> dalla cartella Download e installa Rally Navigator.',
      'Avvia Rally Navigator, fai clic su <b class="text-white">Il mio abbonamento</b> e poi su <b class="text-white">Crea Account</b>.',
      'Fai clic sul link nell’<b class="text-white">email di attivazione</b> che ti inviamo (controlla lo spam) — poi accedi: la tua prova gratuita di 7 giorni è pronta.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows:</b> se compare «Windows ha protetto il PC», fai clic su <b class="text-white">Ulteriori informazioni → Esegui comunque</b>. Il programma di installazione è firmato da Rally Navigator, LLC.',
      mac: '<b class="text-white">Mac:</b> apri il .dmg e trascina Rally Navigator in Applicazioni. Alla prima apertura macOS chiede di confermare l’apertura di un’app scaricata da Internet — fai clic su <b class="text-white">Apri</b>. L’app è firmata e notarizzata da Apple.',
      linux:
        '<b class="text-white">Linux:</b> rendi eseguibile l’AppImage — clic destro → Proprietà → Permessi → <b class="text-white">Consenti l’esecuzione del file come programma</b> (oppure <code>chmod +x</code>) — poi fai doppio clic.',
    },
    didntStart: 'Il download non è partito?',
    clickHere: 'Fai clic qui',
    close: 'Chiudi',
  },
  de: {
    title: 'Ihr Download Startet',
    subtitle: 'Rally Navigator für {os}',
    steps: [
      'Öffnen Sie <b class="text-white">{file}</b> aus Ihrem Download-Ordner und installieren Sie Rally Navigator.',
      'Starten Sie Rally Navigator, klicken Sie auf <b class="text-white">Mein Abo</b> und dann auf <b class="text-white">Konto erstellen</b>.',
      'Klicken Sie auf den Link in der <b class="text-white">Aktivierungs-E-Mail</b>, die wir Ihnen senden (Spam-Ordner prüfen) — dann anmelden: Ihre 7-tägige kostenlose Testphase ist bereit.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows:</b> Wenn „Der Computer wurde durch Windows geschützt“ erscheint, klicken Sie auf <b class="text-white">Weitere Informationen → Trotzdem ausführen</b>. Das Installationsprogramm ist von Rally Navigator, LLC signiert.',
      mac: '<b class="text-white">Mac:</b> Öffnen Sie die .dmg-Datei und ziehen Sie Rally Navigator in den Ordner „Programme“. Beim ersten Öffnen fragt macOS, ob Sie eine aus dem Internet geladene App öffnen möchten — klicken Sie auf <b class="text-white">Öffnen</b>. Die App ist signiert und von Apple notarisiert.',
      linux:
        '<b class="text-white">Linux:</b> Machen Sie das AppImage ausführbar — Rechtsklick → Eigenschaften → Zugriffsrechte → <b class="text-white">Ausführen der Datei als Programm erlauben</b> (oder <code>chmod +x</code>) — und doppelklicken Sie es dann.',
    },
    didntStart: 'Download nicht gestartet?',
    clickHere: 'Hier klicken',
    close: 'Schließen',
  },
  nl: {
    title: 'Uw Download Begint',
    subtitle: 'Rally Navigator voor {os}',
    steps: [
      'Open <b class="text-white">{file}</b> uit uw map Downloads en installeer Rally Navigator.',
      'Start Rally Navigator (de app is in het Engels) en klik op <b class="text-white">My Subscription → Login / Register</b> om uw account aan te maken.',
      'Klik op de link in de <b class="text-white">activeringsmail</b> die we u sturen (kijk ook in uw spam) — log daarna in: uw gratis proefperiode van 7 dagen staat klaar.',
    ],
    notes: {
      windows:
        '<b class="text-white">Windows:</b> ziet u „Windows heeft uw pc beschermd”, klik dan op <b class="text-white">Meer informatie → Toch uitvoeren</b>. Het installatieprogramma is ondertekend door Rally Navigator, LLC.',
      mac: '<b class="text-white">Mac:</b> open de .dmg en sleep Rally Navigator naar Apps. De eerste keer vraagt macOS of u een van internet gedownloade app wilt openen — klik op <b class="text-white">Open</b>. De app is ondertekend en door Apple genotariseerd.',
      linux:
        '<b class="text-white">Linux:</b> maak de AppImage uitvoerbaar — rechtsklik → Eigenschappen → Rechten → <b class="text-white">Uitvoeren van bestand als programma toestaan</b> (of <code>chmod +x</code>) — en dubbelklik er dan op.',
    },
    didntStart: 'Begint de download niet?',
    clickHere: 'Klik hier',
    close: 'Sluiten',
  },
};

export default {
  name: 'DownloadStartedPanel',
  data() {
    return { platform: null, url: null };
  },
  computed: {
    lang() {
      const l = this.$route && this.$route.meta && this.$route.meta.lang;
      return TEXT[l] ? l : 'en';
    },
    t() {
      return TEXT[this.lang];
    },
    installer() {
      const base = INSTALLERS[this.platform] || INSTALLERS.windows;
      if (!this.url) return base;
      return { ...base, url: this.url, file: decodeURIComponent(this.url.split('/').pop()) };
    },
    steps() {
      return this.t.steps.map((s) => s.replace('{file}', this.installer.file));
    },
  },
  mounted() {
    window.addEventListener(DOWNLOAD_STARTED_EVENT, this.open);
    window.addEventListener('keydown', this.onKey);
  },
  beforeUnmount() {
    window.removeEventListener(DOWNLOAD_STARTED_EVENT, this.open);
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    open(e) {
      const p = e && e.detail && e.detail.platform;
      if (!INSTALLERS[p]) return;
      this.platform = p;
      this.url = (e.detail && e.detail.url) || null;
      this.$nextTick(() => this.$refs.dialog && this.$refs.dialog.focus());
    },
    close() {
      this.platform = null;
    },
    onKey(e) {
      if (e.key === 'Escape' && this.platform) this.close();
    },
  },
};
</script>
